import React from 'react';
import { ReadOnlyGuard, SparkIcon, TooltipTrigger } from '@spark';
import FieldsTagsRow from './FieldsTagsRow';

const PromptTextArea = ({ value, placeholder, onChange, ariaLabel }) => (
    <textarea
        className="pg-prompt-panel__textarea"
        value={value || ''}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
    />
);

const PromptPanel = ({
    prompts,
    onChange,
    headerActions,
    fields,
}) => (
    <div className="pg-prompt-panel">
        <div className="pg-prompt-panel__section">
            <div className="pg-prompt-panel__header">
                <span className="pg-prompt-panel__label">SYSTEM PROMPT</span>
                <div className="pg-prompt-panel__actions">{headerActions}</div>
            </div>
            <PromptTextArea
                value={prompts.system}
                placeholder="Add a system prompt"
                ariaLabel="System prompt"
                onChange={(v) => onChange({ ...prompts, system: v })}
            />
        </div>
        <div className="pg-prompt-panel__section">
            <span className="pg-prompt-panel__label">USER PROMPT</span>
            <PromptTextArea
                value={prompts.user}
                placeholder="Add a user prompt — use {{example}} for the input text"
                ariaLabel="User prompt"
                onChange={(v) => onChange({ ...prompts, user: v })}
            />
            {/* Dynamic fields the prompts reference (Figma 2099:71135);
                hidden when there are none. */}
            <FieldsTagsRow fields={fields?.all} unknown={fields?.unknown} />
        </div>
    </div>
);

/**
 * Prompts area (Figma 1314:57779 shared / 1306:56825 split).
 *
 * - Collapsed: 46px "System & User Prompt" row with one truncated preview
 *   per model column (Figma 1314:57656).
 * - Shared: one panel spanning the model columns, with "Split prompts" and
 *   "Replace task prompt" actions.
 * - Split: one panel per model with "Use for all" + "Replace" actions.
 *   Per designer note, the split/use-for-all links are hidden with a single
 *   model column.
 */
const NO_PROMPT_NOTE = 'Runs on raw input - no prompt needed.';

/** Figma `.Playground-Student-No-Prompt` — SpecificAI fine-tuned models on
 *  non-generative tasks take raw input, so their column shows a note. */
const NoPromptPanel = ({ style }) => (
    <div className="pg-prompt-panel pg-prompt-panel--note" style={style}>
        {NO_PROMPT_NOTE}
    </div>
);

/** Walk slots in column order and split them into `note` segments (one per
 *  no-prompt slot) and `shared` segments (contiguous runs of prompt-capable
 *  slots covered by a single shared panel). `start` is the slot index. */
const buildSharedSegments = (slots, isNoPrompt) => {
    const segments = [];
    let run = null;
    slots.forEach((slot, idx) => {
        if (isNoPrompt(slot)) {
            run = null;
            segments.push({ type: 'note', start: idx, slotId: slot.slotId });
        } else if (run) {
            run.span += 1;
        } else {
            run = { type: 'shared', start: idx, span: 1 };
            segments.push(run);
        }
    });
    return segments;
};

const PromptsSection = ({
    collapsed,
    onToggleCollapsed,
    slots,
    noPromptSlotIds,
    splitMode,
    sharedPrompts,
    onSharedPromptsChange,
    slotPrompts,
    onSlotPromptsChange,
    onSplitPrompts,
    onUseForAll,
    onReplaceTaskPrompt,
    // slotId -> { all, known, unknown } dynamic fields referenced by that
    // slot's prompts. In shared mode every prompt slot has the same prompt,
    // so the shared panel shows the first prompt slot's fields.
    fieldsBySlot,
}) => {
    const isNoPrompt = (slot) => !!noPromptSlotIds?.has(slot.slotId);
    const promptSlots = slots.filter((slot) => !isNoPrompt(slot));
    const showSplitControls = promptSlots.length > 1;
    const fieldsFor = (slot) => (slot ? fieldsBySlot?.[slot.slotId] : undefined);
    const sharedFields = fieldsFor(promptSlots[0]);

    if (collapsed) {
        const previewFor = (slot) => {
            if (isNoPrompt(slot)) return NO_PROMPT_NOTE;
            const p = splitMode ? slotPrompts[slot.slotId] : sharedPrompts;
            return [p?.system, p?.user].filter(Boolean).join(' ');
        };
        return (
            <div className="pg-prompts-collapsed fs-unmask">
                <div className="pg-prompts-row__label-col">
                    <button
                        type="button"
                        className="pg-prompts-row__toggle"
                        onClick={onToggleCollapsed}
                        aria-expanded={false}
                    >
                        <SparkIcon slug="chevron-down" size="small" />
                        {'System & User Prompt'}
                    </button>
                </div>
                {slots.map((slot) => (
                    <span className="pg-prompts-collapsed__preview" key={slot.slotId}>
                        {previewFor(slot)}
                    </span>
                ))}
            </div>
        );
    }

    const sharedActions = (
        <>
            {showSplitControls && (
                <TooltipTrigger text="Create a separate prompt for each model" placement="bottom">
                    <button type="button" className="pg-link-neutral" onClick={onSplitPrompts}>
                        Split prompts
                    </button>
                </TooltipTrigger>
            )}
            <ReadOnlyGuard>
                <TooltipTrigger
                    text="Save this prompt as the task prompt"
                    placement="bottom"
                >
                    <button type="button" className="pg-link" onClick={() => onReplaceTaskPrompt(null)}>
                        Replace task prompt
                    </button>
                </TooltipTrigger>
            </ReadOnlyGuard>
        </>
    );

    return (
        <div className="pg-prompts-row fs-unmask">
            <div className="pg-prompts-row__label-col">
                <button
                    type="button"
                    className="pg-prompts-row__toggle"
                    onClick={onToggleCollapsed}
                    aria-expanded
                >
                    <SparkIcon slug="chevron-up" size="small" />
                    Prompts
                </button>
            </div>
            {!splitMode ? (
                <>
                    {/* Place each panel in its slot's grid column (column 1 is
                        the label) so the no-prompt note lands under its own
                        model. Contiguous prompt-capable slots share one panel
                        spanning their columns. */}
                    {(() => {
                        const segments = buildSharedSegments(slots, isNoPrompt);
                        const firstShared = segments.find((s) => s.type === 'shared');
                        return segments.map((segment) =>
                            segment.type === 'note' ? (
                                <NoPromptPanel
                                    key={segment.slotId}
                                    style={{ gridColumn: `${segment.start + 2} / span 1` }}
                                />
                            ) : (
                                <div
                                    key={`shared-${segment.start}`}
                                    className="pg-prompts-row__panel-span"
                                    style={{ gridColumn: `${segment.start + 2} / span ${segment.span}` }}
                                >
                                    <PromptPanel
                                        prompts={sharedPrompts}
                                        onChange={onSharedPromptsChange}
                                        headerActions={segment === firstShared ? sharedActions : null}
                                        fields={sharedFields}
                                    />
                                </div>
                            )
                        );
                    })()}
                </>
            ) : (
                <>
                    {slots.map((slot) => (
                        isNoPrompt(slot) ? (
                            <NoPromptPanel key={slot.slotId} />
                        ) : (
                        <PromptPanel
                            key={slot.slotId}
                            prompts={slotPrompts[slot.slotId] || { system: '', user: '' }}
                            onChange={(next) => onSlotPromptsChange(slot.slotId, next)}
                            fields={fieldsFor(slot)}
                            headerActions={
                                <>
                                    {showSplitControls && (
                                        <TooltipTrigger
                                            text="Use this prompt as the shared prompt for all models"
                                            placement="bottom"
                                        >
                                            <button
                                                type="button"
                                                className="pg-link-neutral"
                                                onClick={() => onUseForAll(slot.slotId)}
                                            >
                                                Use for all
                                            </button>
                                        </TooltipTrigger>
                                    )}
                                    <ReadOnlyGuard>
                                        <TooltipTrigger
                                            text="Save this prompt as the task prompt"
                                            placement="bottom"
                                        >
                                            <button
                                                type="button"
                                                className="pg-link"
                                                onClick={() => onReplaceTaskPrompt(slot.slotId)}
                                            >
                                                Replace
                                            </button>
                                        </TooltipTrigger>
                                    </ReadOnlyGuard>
                                </>
                            }
                        />
                        )
                    ))}
                </>
            )}
        </div>
    );
};

export default PromptsSection;
